import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { clarity } from '../lib/clarity';

const AuthContext = createContext(null);

async function fetchAdminFlag(token) {
  const res = await fetch('/api/admin/stats?check=true', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.status === 200;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (cancelled) return;
        setSession(s);
        setUser(s?.user ?? null);
        // Ties this Clarity session to the Supabase user id (not email — see
        // docs/ui:ux_feature.md §2.5) so an admin can pull up every recording
        // for a customer who reported an issue, not just the current one.
        if (s?.user?.id) clarity.identify(s.user.id);
        if (s?.access_token) {
          try {
            const admin = await fetchAdminFlag(s.access_token);
            if (!cancelled) setIsAdmin(admin);
          } catch {
            if (!cancelled) setIsAdmin(false);
          }
        }
      } catch {
        // getSession itself failed (network error, etc.) — treat as logged out
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (cancelled) return;
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user?.id) clarity.identify(s.user.id);
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveringPassword(true);
      } else if (event === 'SIGNED_OUT') {
        setIsRecoveringPassword(false);
      }
      if (s?.access_token) {
        try {
          const admin = await fetchAdminFlag(s.access_token);
          if (!cancelled) setIsAdmin(admin);
        } catch {
          if (!cancelled) setIsAdmin(false);
        }
      } else {
        if (!cancelled) setIsAdmin(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = useCallback(async ({ email, password, name, phone, instagram, marketingOptIn }) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        data: {
          full_name: (name || '').trim() || undefined,
          phone: (phone || '').trim() || undefined,
          instagram: instagram || undefined,
          // Consent from the register checkbox; MailingAccountCard turns it into
          // a mailing subscription once the user is signed in
          marketing_opt_in: marketingOptIn ? true : undefined,
          marketing_opt_in_at: marketingOptIn ? new Date().toISOString() : undefined
        },
        emailRedirectTo: origin ? `${origin}/account` : undefined
      }
    });
    if (error) throw error;
    return data;
  }, []);

  const signInWithPassword = useCallback(async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password
    });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setIsRecoveringPassword(false);
  }, []);

  const resetPasswordForEmail = useCallback(async ({ email }) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const { error } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      { redirectTo: origin ? `${origin}/account` : undefined }
    );
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async ({ password }) => {
    const { data, error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
    setIsRecoveringPassword(false);
    return data;
  }, []);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    isAdmin,
    isRecoveringPassword,
    signUp,
    signInWithPassword,
    signOut,
    resetPasswordForEmail,
    updatePassword,
    isAuthenticated: !!user,
  }), [user, session, loading, isAdmin, isRecoveringPassword, signUp, signInWithPassword, signOut, resetPasswordForEmail, updatePassword]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
